if (!customElements.get('floating-add-to-cart')) {
  customElements.define(
    'floating-add-to-cart',
    class FloatingAddToCart extends HTMLElement {
      constructor() {
        super();

        this.mobileQuery = window.matchMedia('(max-width: 749px)');
        this.onMobileChange = this.onMobileChange.bind(this);
        this.onIntersection = this.onIntersection.bind(this);
        this.onFloatingClick = this.onFloatingClick.bind(this);
        this.syncFromMainButton = this.syncFromMainButton.bind(this);
        this.syncProductInfo = this.syncProductInfo.bind(this);
        this.syncPriceFromMain = this.syncPriceFromMain.bind(this);
        this.updatePriceFromVariant = this.updatePriceFromVariant.bind(this);
        this.updateSizeState = this.updateSizeState.bind(this);
      }

      connectedCallback() {
        this.sectionId = this.dataset.sectionId;
        this.mainButton = document.getElementById(`ProductSubmitButton-${this.sectionId}`);
        this.floatingButton = this.querySelector('.floating-add-to-cart__button');
        this.floatingLabel = this.querySelector('.product-form__submit-label');
        this.floatingPrice = this.querySelector('.floating-add-to-cart__price');
        this.floatingVariant = this.querySelector('[data-floating-variant-label]');
        this.mainPrice = document.getElementById(`price-${this.sectionId}`);

        if (!this.mainButton || !this.floatingButton) {
          this.hidden = true;
          return;
        }

        this.intersectionObserver = new IntersectionObserver(this.onIntersection, {
          root: null,
          threshold: 0,
        });
        this.intersectionObserver.observe(this.mainButton);

        this.mainButtonObserver = new MutationObserver(this.syncFromMainButton);
        this.mainButtonObserver.observe(this.mainButton, {
          attributes: true,
          attributeFilter: ['disabled', 'aria-disabled', 'class', 'data-select-size'],
          subtree: true,
          characterData: true,
          childList: true,
        });

        if (this.mainPrice) {
          this.priceObserver = new MutationObserver(this.syncProductInfo);
          this.priceObserver.observe(this.mainPrice, {
            subtree: true,
            childList: true,
            characterData: true,
          });
        }

        this.floatingButton.addEventListener('click', this.onFloatingClick);
        this.mobileQuery.addEventListener('change', this.onMobileChange);

        if (typeof subscribe === 'function') {
          this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
            if (data.sectionId !== this.sectionId) return;
            if (data.variant) {
              this.updatePriceFromVariant(data.variant);
            } else {
              this.syncPriceFromMain();
            }
            this.syncVariantLabel();
            this.updateSizeState();
            this.updateVisibility();
          });
        }

        this.mainButtonVisible = false;
        this.syncFromMainButton();
        this.syncProductInfo();
        this.updateSizeState();
        this.onMobileChange();
      }

      disconnectedCallback() {
        this.intersectionObserver?.disconnect();
        this.mainButtonObserver?.disconnect();
        this.priceObserver?.disconnect();
        this.variantChangeUnsubscriber?.();
        this.floatingButton?.removeEventListener('click', this.onFloatingClick);
        this.mobileQuery.removeEventListener('change', this.onMobileChange);
      }

      onIntersection(entries) {
        this.mainButtonVisible = entries.some((entry) => entry.isIntersecting);
        this.updateVisibility();
      }

      onMobileChange() {
        if (!this.mobileQuery.matches) {
          this.classList.remove('is-visible');
          return;
        }
        this.updateVisibility();
      }

      updateVisibility() {
        if (!this.mobileQuery.matches) return;

        const shouldShow = !this.mainButtonVisible;
        this.classList.toggle('is-visible', shouldShow);
        this.hidden = false;
      }

      needsSizeSelection() {
        return (
          window.ProductSizeSelection?.hasSizeOption(this.sectionId) &&
          !window.ProductSizeSelection?.isSizeSelected(this.sectionId)
        );
      }

      onFloatingClick() {
        if (this.needsSizeSelection()) {
          window.ProductSizeSelection?.scrollToSize(this.sectionId);
          return;
        }

        if (
          this.mainButton.disabled ||
          this.mainButton.getAttribute('aria-disabled') === 'true' ||
          this.mainButton.classList.contains('loading')
        ) {
          return;
        }

        this.mainButton.click();
      }

      updateSizeState() {
        const needsSize = this.needsSizeSelection();

        if (needsSize) {
          this.floatingButton.removeAttribute('disabled');
          if (this.floatingLabel) {
            this.floatingLabel.textContent = window.variantStrings?.selectSize || 'Select a size';
          }
        } else {
          this.syncFromMainButton();
        }

        this.syncProductInfo();
      }

      syncProductInfo() {
        this.syncPriceFromMain();
        this.syncVariantLabel();
      }

      syncVariantLabel() {
        if (this.floatingVariant && window.ProductSizeSelection) {
          this.floatingVariant.textContent = window.ProductSizeSelection.getVariantDisplayLabel(this.sectionId);
        }
      }

      formatMoney(cents) {
        const moneyFormat = this.dataset.moneyFormat;

        if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function' && moneyFormat) {
          return Shopify.formatMoney(cents, moneyFormat);
        }

        return '$' + (cents / 100).toFixed(0);
      }

      updatePriceFromVariant(variant) {
        if (!variant || !this.floatingPrice) return;

        const currentEl = this.floatingPrice.querySelector('.floating-bar-price__current');
        const compareEl = this.floatingPrice.querySelector('.floating-bar-price__compare');

        if (currentEl) {
          currentEl.textContent = this.formatMoney(variant.price);
        }

        if (compareEl) {
          if (variant.compare_at_price && variant.compare_at_price > variant.price) {
            compareEl.textContent = this.formatMoney(variant.compare_at_price);
            compareEl.hidden = false;
          } else {
            compareEl.textContent = '';
            compareEl.hidden = true;
          }
        }
      }

      syncPriceFromMain() {
        if (!this.mainPrice || !this.floatingPrice) return;

        const mainCurrent =
          this.mainPrice.querySelector('.product-price') ||
          this.mainPrice.querySelector('.price__regular .price-item--regular');
        const mainCompare = this.mainPrice.querySelector('.price__sale s.price-item--regular, .price__sale s');

        const currentEl = this.floatingPrice.querySelector('.floating-bar-price__current');
        const compareEl = this.floatingPrice.querySelector('.floating-bar-price__compare');

        if (currentEl && mainCurrent) {
          currentEl.textContent = mainCurrent.textContent.trim();
        }

        if (compareEl) {
          if (mainCompare?.textContent?.trim()) {
            compareEl.textContent = mainCompare.textContent.trim();
            compareEl.hidden = false;
          } else {
            compareEl.textContent = '';
            compareEl.hidden = true;
          }
        }
      }

      syncFromMainButton() {
        if (this.needsSizeSelection()) {
          this.updateSizeState();
          return;
        }

        const mainLabel = this.mainButton.querySelector('.product-form__submit-label');
        if (mainLabel && this.floatingLabel) {
          this.floatingLabel.textContent = mainLabel.textContent;
        }

        const isDisabled = this.mainButton.disabled;
        this.floatingButton.disabled = isDisabled;
        this.floatingButton.classList.toggle('loading', this.mainButton.classList.contains('loading'));
        this.floatingButton.setAttribute(
          'aria-busy',
          this.mainButton.getAttribute('aria-busy') === 'true'
        );
      }
    }
  );
}
